// src/components/login/RegisterPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from 'lucide-react';
import { auth } from '../../firebase';
import SignupForm from './SignupForm';

const RegisterPage = ({ isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [redirectDestination, setRedirectDestination] = useState(null);
  
  // Check for redirect parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get('redirect');
    if (redirect) {
      setRedirectDestination(redirect);
    }
    
    // Check if there's a pending booking
    const pendingBooking = localStorage.getItem('pendingBooking');
    if (pendingBooking) {
      try {
        const bookingData = JSON.parse(pendingBooking);
        if (bookingData && bookingData.returnPath) {
          setRedirectDestination(bookingData.returnPath.replace('/', ''));
        }
      } catch (e) {
        console.error('Error parsing pending booking:', e);
      }
    }
  }, [location]);
  
  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        // User is already signed in, redirect appropriately
        if (redirectDestination === 'booking') {
          navigate('/book');
        } else {
          navigate('/account');
        }
      }
    });
    
    return () => unsubscribe();
  }, [navigate, redirectDestination]);
  
  const containerClass = isMobile 
    ? "px-4 py-6 mt-20" 
    : "container mx-auto px-6 py-12 mt-24";
  
  const handleSwitchToLogin = () => {
    // Preserve any redirect parameters when switching to login
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');
    
    if (redirectParam) {
      navigate(`/login?redirect=${redirectParam}`);
    } else {
      navigate('/login');
    }
  };
  
  return (
    <div className={`min-h-screen bg-gray-50 flex flex-col ${containerClass}`}>
      <div className="max-w-md mx-auto w-full bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-[#3A98B9] to-[#5FBDDD] text-white">
          <div className="flex items-center justify-center">
            <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
              <User className="h-8 w-8" />
            </div>
          </div>
          <h2 className="mt-4 text-center text-2xl font-bold">
            Create Account
          </h2>
          <p className="mt-1 text-center text-white/80">
            Join us and start booking services
          </p>
          
          {redirectDestination === 'booking' && (
            <div className="mt-3 text-center text-sm bg-white/20 py-2 px-3 rounded-md">
              Create an account to continue with your booking
            </div>
          )}
        </div>
        
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg">
              {error}
            </div>
          )}
          
          <SignupForm 
            onSwitchMode={handleSwitchToLogin}
            setError={setError}
            redirectDestination={redirectDestination}
          />
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;